import { useState } from 'react';
import {
  Button,
  DataTable,
  ErrorState,
  FormField,
  LoadingState,
  Modal,
  Pager,
  ReasonField,
  Select,
  Stat,
  StatusPill,
  TextField,
  isReasonValid,
  type DataTableColumn,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatDate, formatDateTime } from '../../lib/format/dates';
import { formatCents } from '../../lib/format/money';
import { notify } from '../../services/notify';
import { RAILS, type ChefPayout, type LedgerRow, type PayoutStatus, type Rail } from './api';
import { useAdjustBalance, useChefPayouts, useCreateChefPayout, useListerBalance, usePatchChefPayout } from './queries';
import { humanStatus } from './parts';

export const RAIL_LABEL: Record<Rail | 'none', string> = {
  dots: 'Dots',
  stripe_connect: 'Stripe Connect',
  manual: 'Manual',
  none: 'None',
};

export const PAYOUT_TONE: Record<PayoutStatus, 'warning' | 'info' | 'success' | 'danger'> = {
  pending: 'warning',
  in_flight: 'info',
  paid: 'success',
  failed: 'danger',
};

const REASON_LABEL: Record<LedgerRow['reason'], string> = {
  sale: 'Sale',
  adjustment_credit: 'Adjustment (credit)',
  payout: 'Payout',
  order_spend: 'Spent on an order',
  adjustment_debit: 'Adjustment (debit)',
};

const LEDGER_COLUMNS: DataTableColumn<LedgerRow>[] = [
  {
    id: 'when',
    header: 'When',
    width: '160px',
    cell: (r) => <span className="text-muted">{formatDateTime(r.createdAt)}</span>,
  },
  {
    id: 'what',
    header: 'Entry',
    width: '1.4fr',
    wrap: true,
    cell: (r) => (
      <span className="text-[13px]">
        {REASON_LABEL[r.reason] ?? r.reason}
        {r.note && <span className="block text-xs text-muted">{r.note}</span>}
      </span>
    ),
  },
  {
    id: 'amount',
    header: 'Amount',
    width: '120px',
    align: 'right',
    numeric: true,
    cell: (r) => (
      <span className={r.direction === 'credit' ? 'text-green' : 'text-ink'}>
        {r.direction === 'credit' ? '+' : '−'}
        {formatCents(r.amountCents)}
      </span>
    ),
  },
  {
    id: 'after',
    header: 'Balance after',
    width: '130px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCents(r.balanceAfterCents),
  },
];

/**
 * One chef's earnings ledger and payouts (§7.9a). Operators pay out and move a
 * payout through its lifecycle; only an Owner can adjust a balance by hand.
 */
export function ChefLedgerDialog({
  profileId,
  name,
  onClose,
}: {
  profileId: string;
  name: string;
  onClose: () => void;
}) {
  const { locked } = useSession();
  const [page, setPage] = useState(1);
  const balance = useListerBalance(profileId, { pageSize: 10, offset: (page - 1) * 10 });
  const payouts = useChefPayouts({ chefProfileId: profileId, pageSize: 20, offset: 0 });
  const [panel, setPanel] = useState<'none' | 'payout' | 'adjust'>('none');
  const b = balance.data;

  return (
    <Modal isOpen onClose={onClose} size="xl" title={name}>
      {balance.isPending ? (
        <LoadingState label="Loading ledger…" />
      ) : balance.isError ? (
        <ErrorState error={balance.error} onRetry={() => void balance.refetch()} />
      ) : (
        b && (
          <>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div className="grid flex-1 grid-cols-3 gap-2.5">
                <Stat
                  label="Balance"
                  value={formatCents(b.ledgerBalanceCents)}
                  hint={b.inSync ? 'ledger and cache agree' : 'cache out of sync'}
                />
                <Stat
                  label="Oldest unpaid credit"
                  value={b.oldestUndrawnCreditAt ? formatDate(b.oldestUndrawnCreditAt) : '—'}
                />
                <Stat label="Ledger entries" value={String(b.meta.total)} />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  lockedReason={locked('payouts.write')}
                  onClick={() => setPanel(panel === 'payout' ? 'none' : 'payout')}
                >
                  Pay out
                </Button>
                <Button
                  variant="ghost-dark"
                  size="sm"
                  lockedReason={locked('ledger.adjust')}
                  onClick={() => setPanel(panel === 'adjust' ? 'none' : 'adjust')}
                >
                  Adjust balance
                </Button>
              </div>
            </div>

            {panel === 'payout' && (
              <PayoutForm profileId={profileId} balanceCents={b.ledgerBalanceCents} onDone={() => setPanel('none')} />
            )}
            {panel === 'adjust' && <AdjustForm profileId={profileId} onDone={() => setPanel('none')} />}

            <div className="mb-2 mt-2 text-[11px] font-bold uppercase tracking-[.14em] text-muted">Payouts</div>
            <PayoutList rows={payouts.data?.items ?? []} loading={payouts.isPending} />

            <div className="mb-2 mt-5 text-[11px] font-bold uppercase tracking-[.14em] text-muted">Ledger</div>
            <DataTable
              label="Ledger"
              compact
              minWidth={640}
              columns={LEDGER_COLUMNS}
              rows={b.items ?? []}
              getRowId={(r) => r.id}
              emptyLabel="No ledger entries."
            />
            {b.meta.total > 10 && (
              <Pager page={page} pageSize={10} total={b.meta.total} onPageChange={setPage} noun="entries" />
            )}
          </>
        )
      )}
    </Modal>
  );
}

function PayoutForm({
  profileId,
  balanceCents,
  onDone,
}: {
  profileId: string;
  balanceCents: number;
  onDone: () => void;
}) {
  const create = useCreateChefPayout();
  const [amount, setAmount] = useState((balanceCents / 100).toFixed(2));
  const [rail, setRail] = useState<Rail>('dots');
  const cents = Math.round(Number(amount) * 100);
  const valid = Number.isFinite(cents) && cents > 0;

  return (
    <div className="mb-4 rounded-[10px] border border-panel-border p-3.5">
      <div className="grid grid-cols-2 gap-3.5">
        <FormField label="Amount" htmlFor="po-amount" required hint={`Available ${formatCents(balanceCents)}`}>
          <TextField
            id="po-amount"
            surface="white"
            inputMode="decimal"
            leading="$"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </FormField>
        <FormField label="Rail" htmlFor="po-rail" required>
          <Select
            id="po-rail"
            surface="white"
            value={rail}
            options={RAILS.map((r) => ({ value: r, label: RAIL_LABEL[r] }))}
            onChange={(e) => setRail(e.target.value as Rail)}
          />
        </FormField>
      </div>
      {create.error && <ErrorState error={create.error} compact />}
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost-dark" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!valid}
          loading={create.isPending}
          onClick={() =>
            create.mutate(
              { chefProfileId: profileId, amountCents: cents, rail },
              {
                onSuccess: () => {
                  notify.success(`Payout of ${formatCents(cents)} started.`);
                  onDone();
                },
              },
            )
          }
        >
          Start payout
        </Button>
      </div>
    </div>
  );
}

function AdjustForm({ profileId, onDone }: { profileId: string; onDone: () => void }) {
  const adjust = useAdjustBalance();
  const [direction, setDirection] = useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const cents = Math.round(Number(amount) * 100);
  const valid = Number.isFinite(cents) && cents > 0 && isReasonValid(note);

  return (
    <div className="mb-4 rounded-[10px] border border-panel-border p-3.5">
      <div className="grid grid-cols-2 gap-3.5">
        <FormField label="Direction" htmlFor="adj-dir" required>
          <Select
            id="adj-dir"
            surface="white"
            value={direction}
            options={[
              { value: 'credit', label: 'Credit — add to the balance' },
              { value: 'debit', label: 'Debit — take from the balance' },
            ]}
            onChange={(e) => setDirection(e.target.value as 'credit' | 'debit')}
          />
        </FormField>
        <FormField label="Amount" htmlFor="adj-amount" required>
          <TextField
            id="adj-amount"
            surface="white"
            inputMode="decimal"
            leading="$"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </FormField>
      </div>
      <div className="mt-3.5">
        <ReasonField value={note} onChange={setNote} />
      </div>
      {adjust.error && <ErrorState error={adjust.error} compact />}
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost-dark" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!valid}
          loading={adjust.isPending}
          onClick={() =>
            adjust.mutate(
              { profileId, amountCents: cents, note: note.trim(), direction },
              {
                onSuccess: () => {
                  notify.success('Balance adjusted.');
                  onDone();
                },
              },
            )
          }
        >
          Adjust
        </Button>
      </div>
    </div>
  );
}

function payoutColumns(
  lock: string | undefined,
  onStatus: (id: string, status: PayoutStatus) => void,
  onFail: (p: ChefPayout) => void,
): DataTableColumn<ChefPayout>[] {
  const terminal = (s: PayoutStatus) => s === 'paid' || s === 'failed';
  return [
    {
      id: 'when',
      header: 'Requested',
      width: '150px',
      cell: (p) => <span className="text-muted">{formatDate(p.requestedAt)}</span>,
    },
    {
      id: 'amount',
      header: 'Amount',
      width: '110px',
      align: 'right',
      numeric: true,
      cell: (p) => formatCents(p.amountCents),
    },
    { id: 'rail', header: 'Rail', width: '130px', cell: (p) => RAIL_LABEL[p.rail] },
    {
      id: 'status',
      header: 'Status',
      width: '1.2fr',
      wrap: true,
      cell: (p) => (
        <span className="text-[13px]">
          <StatusPill tone={PAYOUT_TONE[p.status]} dot>
            {humanStatus(p.status)}
          </StatusPill>
          {p.failureReason && <span className="mt-0.5 block text-xs text-danger">{p.failureReason}</span>}
        </span>
      ),
    },
    {
      id: 'act',
      header: '',
      width: '220px',
      align: 'right',
      action: true,
      cell: (p) =>
        !terminal(p.status) ? (
          <span className="inline-flex gap-1.5">
            {p.status === 'pending' && (
              <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => onStatus(p.id, 'in_flight')}>
                Sent
              </Button>
            )}
            <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => onStatus(p.id, 'paid')}>
              Paid
            </Button>
            <Button variant="ghost-dark" size="sm" lockedReason={lock} onClick={() => onFail(p)}>
              Failed
            </Button>
          </span>
        ) : null,
    },
  ];
}

/** Payouts with their lifecycle actions: sent → paid, or failed with a reason. */
export function PayoutList({ rows, loading }: { rows: ChefPayout[]; loading?: boolean }) {
  const { locked } = useSession();
  const patch = usePatchChefPayout();
  const [failing, setFailing] = useState<ChefPayout | null>(null);
  const [reason, setReason] = useState('');

  const columns = payoutColumns(locked('payouts.write'), (id, status) => patch.mutate({ id, status }), setFailing);

  return (
    <>
      <DataTable
        label="Payouts"
        compact
        minWidth={700}
        columns={columns}
        rows={rows}
        getRowId={(p) => p.id}
        loading={loading}
        emptyLabel="No payouts yet."
      />
      {patch.error && <ErrorState error={patch.error} compact />}
      {failing && (
        <Modal
          isOpen
          onClose={() => setFailing(null)}
          size="md"
          title="Mark this payout failed?"
          footer={
            <>
              <Button variant="ghost-dark" size="sm" onClick={() => setFailing(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!isReasonValid(reason)}
                loading={patch.isPending}
                onClick={() =>
                  patch.mutate(
                    { id: failing.id, status: 'failed', failureReason: reason.trim() },
                    {
                      onSuccess: () => {
                        notify.success('Payout marked failed — the amount is back on the balance.');
                        setFailing(null);
                        setReason('');
                      },
                    },
                  )
                }
              >
                Mark failed
              </Button>
            </>
          }
        >
          <p className="mt-0 text-sm text-muted">
            {formatCents(failing.amountCents)} goes back onto the chef's balance. This can't be undone.
          </p>
          <ReasonField value={reason} onChange={setReason} label="What went wrong" />
        </Modal>
      )}
    </>
  );
}
